/**
 * RenderControls — the render buttons in the designer's header and the floating
 * windows they open.
 *
 * Each button opens a small window of live controls for one side of how the 3D
 * view is drawn: lighting, shadows, materials, lens effects, camera. "Looks"
 * holds ready-made setups, plus copy / paste of the settings as text. Holding
 * "Before" shows the view as it was drawn before any of this existed.
 *
 * The controls are generated from the schema in ui/room3d/renderSettings.ts, and
 * they write straight to that store: the viewer redraws on every change, without
 * rebuilding the scene. Nothing here touches the room being designed, so none of
 * it is part of undo / redo.
 *
 * Windows are fixed to the browser window (they float over the designer), can be
 * dragged by their title bar, and remember where they were left.
 */

import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Aperture, Camera, ClipboardPaste, Copy, Eclipse, Hammer, Layers, RotateCcw, Sparkles, SplitSquareHorizontal, Sun, Trash2, TreePine, Upload, Wand2, X } from 'lucide-react';
import {
  COMPARE_PRESET_ID,
  ControlDef,
  ControlGroup,
  DEFAULT_RENDER_SETTINGS,
  RENDER_PRESETS,
  RENDER_WINDOWS,
  RenderSettingKey,
  RenderSettings,
  RenderWindowDef,
  RenderWindowId,
  TextureControl,
  applyRenderPreset,
  exportRenderSettings,
  formatSettingValue,
  getRenderPreset,
  importRenderSettings,
  matchingPresetId,
  previewRenderSettings,
  resetRenderSettings,
  setRenderSetting,
  updateRenderSettings,
  withLook,
} from '../../ui/room3d/renderSettings';
import {
  addStoredTexture,
  initTextureLibrary,
  listStoredTextures,
  onTextureLibraryChange,
  removeStoredTexture,
} from '../../ui/room3d/textureLibrary';
import { useRenderSettings } from './useRenderSettings';
import { SimpleLighting } from './SimpleLighting';
import { setLensPreview } from '../../ui/room3d/lensPreview';
import { PhotoRenderBody } from './PhotoRenderBody';
import { Group as Fold } from './Group';
import { FACTORY_PHOTO_RENDER, PhotoRenderSettings, loadPhotoRender, savePhotoRender } from '../../ui/room3d/photoRender';
import type { RenderLook } from '../../ui/room3d/renderLooks';
import type { PlanFinish } from '../../../calculations/plan/floorPlan';
import { photoPreviewSettings } from '../../ui/room3d/photoPreview';

/** Whether the Photoreal window's settings are previewed in the 3D view: this browser's choice, kept. */
const PHOTO_PREVIEW_KEY = 'designer.photoRender.preview';
const loadPhotoPreview = (): boolean => { try { return window.localStorage.getItem(PHOTO_PREVIEW_KEY) === '1'; } catch { return false; } };
import { DESIGNER_STRINGS as S } from '../strings';
import './renderControls.css';

const R = S.render;

/** 'photo': the Photoreal render window — how Blender renders the room, and its Render button. */
type WindowId = RenderWindowId | 'looks' | 'photo';

const WINDOW_ICON: Record<WindowId, typeof Sun> = {
  looks: Wand2,
  lighting: Sun,
  shadows: Eclipse,
  materials: Layers,
  framing: Hammer,
  wood: TreePine,
  effects: Sparkles,
  output: Aperture,
  photo: Camera,
};

const WINDOW_ORDER: WindowId[] = ['looks', 'lighting', 'shadows', 'materials', 'framing', 'wood', 'effects', 'output', 'photo'];
/** The view's own windows, one button each; the Photoreal window's button stands apart, after Compare. */
const VIEW_WINDOWS = WINDOW_ORDER.filter((id) => id !== 'photo');
const windowTitle = (id: WindowId): string => (id === 'looks' ? R.looks : id === 'photo' ? S.photo.title : RENDER_WINDOWS.find((w) => w.id === id)?.title ?? id);
/** The Photoreal window is wider (renderControls.css .is-photo): its libraries are grids of pictures. */
const PHOTO_WIDTH = 400;

// ── Which windows are open and where: kept across reloads ──────────────────

const UI_STORAGE_KEY = 'designer.render.ui.v1';
const WINDOW_WIDTH = 304;

interface WindowsState {
  /** Open windows, back to front. */
  open: WindowId[];
  /** Top-left corner of each window that has been moved (px). */
  position: Partial<Record<WindowId, { x: number; y: number }>>;
}

function loadWindows(): WindowsState {
  try {
    const parsed = JSON.parse(localStorage.getItem(UI_STORAGE_KEY) || 'null');
    if (parsed && Array.isArray(parsed.open) && parsed.position && typeof parsed.position === 'object') {
      return {
        open: parsed.open.filter((id: unknown): id is WindowId => WINDOW_ORDER.includes(id as WindowId)),
        position: parsed.position,
      };
    }
  } catch {
    /* unreadable — start closed */
  }
  return { open: [], position: {} };
}

function saveWindows(state: WindowsState): void {
  try {
    localStorage.setItem(UI_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* not kept — fine */
  }
}

/** Where a window opens if it has never been moved: down the right-hand side, each a step further in. */
function defaultPosition(id: WindowId): { x: number; y: number } {
  // Under the two-row header (2 × 44 px), with a little air.
  if (id === 'photo') return { x: window.innerWidth - PHOTO_WIDTH - 16, y: 102 };
  const i = WINDOW_ORDER.indexOf(id);
  return { x: window.innerWidth - WINDOW_WIDTH - 16 - i * 26, y: 102 + i * 30 };
}

/** Keep a window's title bar reachable whatever the browser window's size. */
function onScreen(p: { x: number; y: number }): { x: number; y: number } {
  return {
    x: Math.max(4, Math.min(window.innerWidth - 80, p.x)),
    y: Math.max(4, Math.min(window.innerHeight - 40, p.y)),
  };
}

// ── Rows ───────────────────────────────────────────────────────────────────

const ResetButton: React.FC<{ onClick: () => void }> = ({ onClick }) => (
  <button type="button" className="rc-reset" title={R.resetValue} aria-label={R.resetValue} onClick={onClick}>
    <RotateCcw size={11} />
  </button>
);

/** Pick the picture a material is drawn with: a built-in, or one you upload. */
const TextureRow: React.FC<{ control: TextureControl; value: string; changed: boolean; onChange: (id: string) => void; onReset: () => void }> = ({ control, value, changed, onChange, onReset }) => {
  const uploads = useSyncExternalStore(onTextureLibraryChange, listStoredTextures);
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { void initTextureLibrary(); }, []);

  const isUpload = uploads.some((u) => u.id === value);
  const known = isUpload || control.builtins.some((b) => b.value === value);

  const upload = async (file: File | undefined) => {
    if (!file) return;
    try {
      const stored = await addStoredTexture(file);
      setError(null);
      onChange(stored.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : R.uploadFailed);
    }
  };

  return (
    <div className="rc-row">
      <div className="rc-row-head">
        <span className="rc-label" title={control.hint}>{control.label}</span>
        {changed && <ResetButton onClick={onReset} />}
        <select className="rc-select" aria-label={control.label} value={known ? value : control.builtins[0].value} onChange={(e) => onChange(e.target.value)}>
          {control.builtins.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          {uploads.length > 0 && (
            <optgroup label={R.uploads}>
              {uploads.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </optgroup>
          )}
        </select>
      </div>
      <div className="rc-actions">
        <button type="button" className="rc-action" onClick={() => input.current?.click()}><Upload size={12} /> {R.upload}</button>
        {isUpload && (
          <button
            type="button"
            className="rc-action"
            onClick={() => { void removeStoredTexture(value); onChange(control.builtins[0].value); }}
          >
            <Trash2 size={12} /> {R.removeUpload}
          </button>
        )}
        <input
          ref={input}
          type="file"
          accept="image/*"
          aria-label={`${R.upload} — ${control.label}`}
          style={{ display: 'none' }}
          onChange={(e) => { void upload(e.target.files?.[0]); e.target.value = ''; }}
        />
      </div>
      {error && <p className="rc-note is-bad">{error}</p>}
    </div>
  );
};

const ControlRow: React.FC<{ control: ControlDef; settings: RenderSettings }> = ({ control, settings }) => {
  const key = control.key;
  const value = settings[key];
  const fallback = DEFAULT_RENDER_SETTINGS[key];
  const changed = value !== fallback;
  const reset = () => updateRenderSettings({ [key]: fallback } as Partial<RenderSettings>);
  const set = (next: RenderSettings[RenderSettingKey]) => updateRenderSettings({ [key]: next } as Partial<RenderSettings>);
  const label = <span className="rc-label" title={control.hint}>{control.label}</span>;

  if (control.type === 'range') {
    return (
      <div className="rc-row">
        <div className="rc-row-head">
          {label}
          {changed && <ResetButton onClick={reset} />}
          <span className="rc-value" onDoubleClick={reset} title={changed ? `${R.resetValue}: ${formatSettingValue(fallback as number, control)}` : undefined}>
            {formatSettingValue(value as number, control)}
          </span>
        </div>
        <input
          className="rc-range"
          type="range"
          aria-label={control.label}
          min={control.min}
          max={control.max}
          step={control.step}
          value={value as number}
          onChange={(e) => set(Number(e.target.value))}
        />
      </div>
    );
  }

  if (control.type === 'texture') {
    return <TextureRow control={control} value={value as string} changed={changed} onChange={(id) => set(id)} onReset={reset} />;
  }

  if (control.type === 'select') {
    return (
      <div className="rc-row">
        <div className="rc-row-head">
          {label}
          {changed && <ResetButton onClick={reset} />}
          <select
            className="rc-select"
            aria-label={control.label}
            value={String(value)}
            onChange={(e) => {
              const option = control.options.find((o) => String(o.value) === e.target.value);
              if (option) set(option.value as RenderSettings[RenderSettingKey]);
            }}
          >
            {control.options.map((o) => <option key={String(o.value)} value={String(o.value)}>{o.label}</option>)}
          </select>
        </div>
      </div>
    );
  }

  if (control.type === 'color') {
    return (
      <div className="rc-row">
        <div className="rc-row-head">
          {label}
          {changed && <ResetButton onClick={reset} />}
          <span className="rc-value">{String(value).toUpperCase()}</span>
          <input className="rc-color" type="color" aria-label={control.label} value={value as string} onChange={(e) => set(e.target.value)} />
        </div>
      </div>
    );
  }

  return (
    <div className="rc-row">
      <div className="rc-row-head">
        {label}
        <input className="rc-switch" type="checkbox" aria-label={control.label} checked={value as boolean} onChange={(e) => set(e.target.checked)} />
      </div>
    </div>
  );
};

const Group: React.FC<{ windowId: WindowId; group: ControlGroup; settings: RenderSettings }> = ({ windowId, group, settings }) => {
  const on = group.toggle ? (settings[group.toggle] as boolean) : true;
  const toggle = group.toggle && (
    <input
      className="rc-switch"
      type="checkbox"
      aria-label={group.title}
      checked={on}
      onChange={(e) => setRenderSetting(group.toggle as RenderSettingKey, e.target.checked as never)}
    />
  );
  return (
    <Fold id={`${windowId}.${group.title}`} title={group.title} note={group.note} off={!on} headEnd={toggle}>
      {group.presets && (
        <div className="rc-actions" style={{ marginTop: 0, marginBottom: 6 }}>
          {group.presets.map((preset) => {
            const active = (Object.keys(preset.values) as RenderSettingKey[]).every((k) => settings[k] === preset.values[k]);
            return (
              <button
                key={preset.label}
                type="button"
                className={`rc-action${active ? ' is-active' : ''}`}
                aria-pressed={active}
                onClick={() => updateRenderSettings(preset.values)}
              >
                {preset.label}
              </button>
            );
          })}
        </div>
      )}
      {group.controls.map((control) => <ControlRow key={control.key} control={control} settings={settings} />)}
    </Fold>
  );
};

/** Settings that are a choice about the window itself, not a value it edits: drawn in its own way, left alone by a reset. */
const PANEL_KEYS = new Set<RenderSettingKey>(['lightingSimple']);

/** Everything a window controls, back at the default look. */
function resetWindow(def: RenderWindowDef): void {
  const patch: Partial<Record<RenderSettingKey, unknown>> = {};
  for (const group of def.groups) {
    if (group.toggle) patch[group.toggle] = DEFAULT_RENDER_SETTINGS[group.toggle];
    for (const control of group.controls) {
      if (!PANEL_KEYS.has(control.key)) patch[control.key] = DEFAULT_RENDER_SETTINGS[control.key];
    }
  }
  updateRenderSettings(patch as Partial<RenderSettings>);
}

// ── Looks ──────────────────────────────────────────────────────────────────

const LooksBody: React.FC<{ settings: RenderSettings }> = ({ settings }) => {
  const active = matchingPresetId(settings);
  const [text, setText] = useState<string | null>(null);
  const [note, setNote] = useState<{ text: string; ok: boolean } | null>(null);

  const copy = async () => {
    const json = exportRenderSettings();
    setText(json);
    try {
      await navigator.clipboard.writeText(json);
      setNote({ text: R.copied, ok: true });
    } catch {
      setNote({ text: R.copyFailed, ok: false });
    }
  };

  const apply = () => {
    const ok = importRenderSettings(text ?? '');
    setNote({ text: ok ? R.pasteOk : R.pasteBad, ok });
  };

  return (
    <>
      {RENDER_PRESETS.map((preset) => (
        <button
          key={preset.id}
          type="button"
          className={`rc-look${active === preset.id ? ' is-active' : ''}`}
          onClick={() => { applyRenderPreset(preset.id); setNote(null); }}
        >
          <span className="rc-look-name">{preset.label}</span>
          <span className="rc-look-desc">{preset.description}</span>
        </button>
      ))}
      <div className="rc-actions">
        <button type="button" className="rc-action" onClick={() => { resetRenderSettings(); setNote(null); }}><RotateCcw size={12} /> {R.resetAll}</button>
        <button type="button" className="rc-action" onClick={copy}><Copy size={12} /> {R.copy}</button>
        <button type="button" className="rc-action" onClick={() => { setText(''); setNote({ text: R.pasteHint, ok: true }); }}><ClipboardPaste size={12} /> {R.paste}</button>
      </div>
      {text !== null && (
        <>
          <textarea className="rc-json" aria-label={R.paste} value={text} spellCheck={false} onChange={(e) => setText(e.target.value)} />
          <div className="rc-actions">
            <button type="button" className="rc-action" onClick={apply}>{R.apply}</button>
            <button type="button" className="rc-action" onClick={() => { setText(null); setNote(null); }}>{R.close}</button>
          </div>
        </>
      )}
      {note && <p className={`rc-note ${note.ok ? 'is-ok' : 'is-bad'}`}>{note.text}</p>}
      <p className="rc-note">{R.looksNote}</p>
    </>
  );
};

// ── A window ───────────────────────────────────────────────────────────────

interface FloatingWindowProps {
  id: WindowId;
  position: { x: number; y: number };
  zIndex: number;
  onMove: (id: WindowId, position: { x: number; y: number }) => void;
  onFocus: (id: WindowId) => void;
  onClose: (id: WindowId) => void;
  onReset?: () => void;
  children: React.ReactNode;
}

const FloatingWindow: React.FC<FloatingWindowProps> = ({ id, position, zIndex, onMove, onFocus, onClose, onReset, children }) => {
  const drag = useRef<{ dx: number; dy: number } | null>(null);
  const Icon = WINDOW_ICON[id];
  const at = onScreen(position);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button')) return;
    drag.current = { dx: e.clientX - at.x, dy: e.clientY - at.y };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    onMove(id, onScreen({ x: e.clientX - drag.current.dx, y: e.clientY - drag.current.dy }));
  };
  const endDrag = () => { drag.current = null; };

  return (
    <div
      className={`rc-window${id === 'photo' ? ' is-photo' : ''}`}
      role="dialog"
      aria-label={windowTitle(id)}
      style={{ left: at.x, top: at.y, zIndex, maxHeight: `calc(100vh - ${Math.round(at.y) + 12}px)` }}
      onPointerDownCapture={() => onFocus(id)}
    >
      <div className="rc-window-head" onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={endDrag} onPointerCancel={endDrag}>
        <span className="rc-window-title"><Icon size={15} /> {windowTitle(id)}</span>
        {onReset && (
          <button type="button" className="rc-icon-btn" title={R.resetWindow} aria-label={R.resetWindow} onClick={onReset}><RotateCcw size={14} /></button>
        )}
        <button type="button" className="rc-icon-btn" title={R.close} aria-label={`${R.close} ${windowTitle(id)}`} onClick={() => onClose(id)}><X size={15} /></button>
      </div>
      <div className="rc-window-body">{children}</div>
    </div>
  );
};

// ── Lighting: the simple panel or every setting ────────────────────────────

const LightingBody: React.FC<{ def: RenderWindowDef; settings: RenderSettings; roomSize?: { length: number; width: number } }> = ({ def, settings, roomSize }) => (
  <>
    <div className="rc-mode" role="tablist" title={R.modeTitle}>
      {([[true, R.simple], [false, R.advanced]] as const).map(([simple, label]) => (
        <button
          key={label}
          type="button"
          role="tab"
          aria-selected={settings.lightingSimple === simple}
          className={`rc-mode-btn${settings.lightingSimple === simple ? ' is-active' : ''}`}
          onClick={() => setRenderSetting('lightingSimple', simple as never)}
        >
          {label}
        </button>
      ))}
    </div>
    {settings.lightingSimple
      ? <SimpleLighting settings={settings} roomSize={roomSize} />
      : def.groups.map((group) => (
        <Group key={group.title} windowId={def.id} group={{ ...group, controls: group.controls.filter((c) => !PANEL_KEYS.has(c.key)) }} settings={settings} />
      ))}
  </>
);

// ── The bar ────────────────────────────────────────────────────────────────

export interface RenderControlsProps {
  /** The room shown (feet): the simple lighting panel draws its plan and counts its lights. */
  roomSize?: { length: number; width: number };
  /** Save the room for the Blender renderer with these photoreal settings: the Photoreal window's Render button. */
  onRender?: (photo: PhotoRenderSettings) => Promise<string | void>;
  /** What Stage drew for the render (a floor set, an HDRI), shown where it applies. */
  staged?: PlanFinish;
  /**
   * The space's render look (the Look Lab's, picked for this room): its
   * photoreal settings are the window's, and the view previews them from the
   * start — the floor, the HDRI and the lighting the render will have.
   */
  look?: RenderLook;
}

export const RenderControls: React.FC<RenderControlsProps> = ({ roomSize, onRender, staged, look: spaceLook }) => {
  const settings = useRenderSettings();
  // The photoreal render's settings: the space's look when it has one, else this browser's, kept between visits; sent with every export.
  const [photo, setPhoto] = useState<PhotoRenderSettings>(() => spaceLook?.photo ?? loadPhotoRender());
  useEffect(() => { savePhotoRender(photo); }, [photo]);
  // The preview: the photoreal settings laid over the view's own look, live (photoPreview.ts). On, with a space look.
  const [photoPreview, setPhotoPreview] = useState<boolean>(() => (spaceLook ? true : loadPhotoPreview()));
  // Another space look (another space, the library reloaded): its settings, shown.
  useEffect(() => { if (spaceLook) { setPhoto(spaceLook.photo); setPhotoPreview(true); } }, [spaceLook]);
  useEffect(() => { try { window.localStorage.setItem(PHOTO_PREVIEW_KEY, photoPreview ? '1' : '0'); } catch { /* not kept — fine */ } }, [photoPreview]);
  const photoView = useCallback(() => (photoPreview ? photoPreviewSettings(settings, photo) : null), [photoPreview, settings, photo]);
  useEffect(() => { previewRenderSettings(photoView()); }, [photoView]);
  // The lens too (lensPreview.ts): a normal lens's focal length shows in the 3D view while previewing.
  const previewMm = photoPreview && photo.camera.lensType === 'perspective' ? photo.camera.focalMm : null;
  useEffect(() => { setLensPreview(previewMm); }, [previewMm]);
  useEffect(() => () => setLensPreview(null), []);
  // "Before" is lit while it is held — not whenever the view shows something
  // other than the saved look (the photoreal preview does that too).
  const [comparing, setComparing] = useState(false);
  const [windows, setWindows] = useState<WindowsState>(loadWindows);

  useEffect(() => { saveWindows(windows); }, [windows]);
  // Never leave the "before" view up if the bar goes away mid-hold.
  useEffect(() => () => previewRenderSettings(null), []);

  const toggle = useCallback((id: WindowId) => {
    setWindows((w) => ({ ...w, open: w.open.includes(id) ? w.open.filter((o) => o !== id) : [...w.open, id] }));
  }, []);
  const close = useCallback((id: WindowId) => {
    setWindows((w) => ({ ...w, open: w.open.filter((o) => o !== id) }));
  }, []);
  const focus = useCallback((id: WindowId) => {
    setWindows((w) => (w.open[w.open.length - 1] === id ? w : { ...w, open: [...w.open.filter((o) => o !== id), id] }));
  }, []);
  const move = useCallback((id: WindowId, position: { x: number; y: number }) => {
    setWindows((w) => ({ ...w, position: { ...w.position, [id]: position } }));
  }, []);

  const compare = getRenderPreset(COMPARE_PRESET_ID);
  const holdCompare = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!compare) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setComparing(true);
    previewRenderSettings(withLook(settings, compare.settings));
  };
  // Back to what was showing: the photoreal preview when it is on, else the view as it is.
  const releaseCompare = () => { setComparing(false); previewRenderSettings(photoView()); };

  const look = matchingPresetId(settings);
  const lookName = look ? getRenderPreset(look)?.label ?? R.custom : R.custom;

  return (
    <div className="rc-bar">
      {VIEW_WINDOWS.map((id) => {
        const Icon = WINDOW_ICON[id];
        const title = id === 'looks' ? R.looksTitle : R.openWindow(windowTitle(id));
        return (
          <button
            key={id}
            type="button"
            className={`rc-btn${windows.open.includes(id) ? ' is-open' : ''}`}
            title={title}
            aria-label={title}
            aria-pressed={windows.open.includes(id)}
            onClick={() => toggle(id)}
          >
            <Icon size={15} />
            <span className="rc-btn-label">{id === 'looks' ? `${R.looks}: ${lookName}` : windowTitle(id)}</span>
          </button>
        );
      })}
      <span className="rc-bar-divider" />
      <button
        type="button"
        className={`rc-btn${comparing ? ' is-held' : ''}`}
        title={R.compareTitle}
        aria-label={R.compareTitle}
        onPointerDown={holdCompare}
        onPointerUp={releaseCompare}
        onPointerCancel={releaseCompare}
        onLostPointerCapture={releaseCompare}
      >
        <SplitSquareHorizontal size={15} />
        <span className="rc-btn-label">{R.compare}</span>
      </button>
      <span className="rc-bar-divider" />
      <button
        type="button"
        className={`rc-btn${windows.open.includes('photo') ? ' is-open' : ''}`}
        title={S.photo.openTitle}
        aria-label={S.photo.openTitle}
        aria-pressed={windows.open.includes('photo')}
        onClick={() => toggle('photo')}
      >
        <Camera size={15} />
        <span className="rc-btn-label">{S.photo.render}</span>
      </button>

      {/* Always in the same DOM order — bringing a window forward only changes its
          z-index, so a slider being dragged is never moved under the pointer. */}
      {WINDOW_ORDER.filter((id) => windows.open.includes(id)).map((id) => {
        const def = RENDER_WINDOWS.find((w) => w.id === id);
        return (
          <FloatingWindow
            key={id}
            id={id}
            position={windows.position[id] ?? defaultPosition(id)}
            zIndex={60 + windows.open.indexOf(id)}
            onMove={move}
            onFocus={focus}
            onClose={close}
            onReset={def ? () => resetWindow(def) : id === 'photo' ? () => setPhoto(FACTORY_PHOTO_RENDER) : undefined}
          >
            {id === 'photo'
              ? <PhotoRenderBody settings={photo} onChange={setPhoto} seamStrength={settings.seamStrength} onRender={onRender ? () => onRender(photo) : undefined} preview={photoPreview} onPreview={setPhotoPreview} staged={staged} look={{ warmth: settings.interiorWarmth, intensity: settings.interiorIntensity, ambient: settings.ambientIntensity, environment: settings.environment, exposure: settings.exposure, seamStrength: settings.seamStrength }} />
              : !def
              ? <LooksBody settings={settings} />
              : def.id === 'lighting'
                ? <LightingBody def={def} settings={settings} roomSize={roomSize} />
                : def.groups.map((group) => <Group key={group.title} windowId={def.id} group={group} settings={settings} />)}
          </FloatingWindow>
        );
      })}
    </div>
  );
};

export default RenderControls;
